"use client";

import { c as _c } from "react/compiler-runtime";
import React, { type JSX, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetWaveData } from "@/contexts/TitleContext";
import { useContentTab } from "../ContentTabContext";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import MyStreamWaveChat from "./MyStreamWaveChat";
import MyStreamWaveCurationContent from "./curations/MyStreamWaveCurationContent";
import { useWaveData } from "@/hooks/useWaveData";
import MyStreamWaveLeaderboard from "./MyStreamWaveLeaderboard";
import MyStreamWaveSubmissions from "./MyStreamWaveSubmissions";
import MyStreamWaveOutcome from "./MyStreamWaveOutcome";
import { useSearchParams, usePathname, useRouter } from "next/navigation";
import { WaveWinners } from "@/components/waves/winners/WaveWinners";
import { MyStreamWaveTab } from "@/types/waves.types";
import { MyStreamWaveTabs } from "./tabs/MyStreamWaveTabs";
import MyStreamWaveMyVotes from "./votes/MyStreamWaveMyVotes";
import MyStreamWaveFAQ from "./MyStreamWaveFAQ";
import MyStreamWaveSales from "./MyStreamWaveSales";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { createBreakpoint } from "react-use";
import { getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { useWaveViewMode } from "@/hooks/useWaveViewMode";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { getDropQueryKey } from "@/services/api/drop-api";
export interface MyStreamWaveProps {
  readonly waveId: string;
}
const getContentTabPanelId = (tab: MyStreamWaveTab): string => `my-stream-wave-tabpanel-${tab.toLowerCase()}`;
const useBreakpoint = createBreakpoint({
  LG: 1024,
  S: 0
});
const MyStreamWaveContent: React.FC<MyStreamWaveProps> = t0 => {
  const $ = _c(97);
  const {
    waveId
  } = t0;
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();
  const {
    isApp
  } = useDeviceInfo();
  const queryClient = useQueryClient();
  const {
    waves,
    directMessages,
    registerWave
  } = useMyStream();
  let t1;
  if ($[0] !== isApp || $[1] !== pathname || $[2] !== router || $[3] !== searchParams) {
    t1 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[0] = isApp;
    $[1] = pathname;
    $[2] = router;
    $[3] = searchParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t1 || $[6] !== waveId) {
    t2 = {
      waveId,
      onWaveNotFound: t1
    };
    $[5] = t1;
    $[6] = waveId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const {
    data: wave
  } = useWaveData(t2);
  let t3;
  let t4;
  if ($[8] !== registerWave || $[9] !== waveId) {
    t3 = () => {
      registerWave(waveId, true);
    };
    t4 = [registerWave, waveId];
    $[8] = registerWave;
    $[9] = waveId;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== directMessages.list || $[13] !== waveId || $[14] !== waves.list) {
    t5 = waves.list.find(w_0 => w_0.id === waveId) ?? directMessages.list.find(w => w.id === waveId);
    $[12] = directMessages.list;
    $[13] = waveId;
    $[14] = waves.list;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const waveFromList = t5;
  const t6 = waveFromList?.newDropsCount.count ?? 0;
  const t7 = waveFromList?.firstUnreadDropSerialNo ?? null;
  let t8;
  if ($[16] !== t6 || $[17] !== t7) {
    t8 = {
      newDropsCount: t6,
      firstUnreadSerialNo: t7
    };
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const enhancedData = t8;
  const newDropsCount = enhancedData.newDropsCount;
  let t9;
  if ($[19] !== newDropsCount || $[20] !== wave) {
    t9 = wave ? {
      name: wave.name,
      newItemsCount: newDropsCount
    } : null;
    $[19] = newDropsCount;
    $[20] = wave;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  useSetWaveData(t9);
  const stableWaveKey = `wave-${waveId}`;
  const {
    activeContentTab
  } = useContentTab();
  let t10;
  if ($[22] !== searchParams) {
    t10 = searchParams.get("curation");
    $[22] = searchParams;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const activeCurationId = t10;
  const {
    viewMode,
    setViewMode,
    toggleViewMode
  } = useWaveViewMode(waveId);
  const {
    isRankWave,
    isApproveWave,
    isMemesWave,
    isDm
  } = useWave(wave);
  const showGalleryToggle = !isRankWave && !isApproveWave && !isMemesWave && !isDm;
  let t11;
  if ($[24] !== searchParams) {
    t11 = searchParams.get("serialNo");
    $[24] = searchParams;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const hasSerialTarget = t11 !== null;
  let t12;
  let t13;
  if ($[26] !== hasSerialTarget || $[27] !== setViewMode || $[28] !== showGalleryToggle || $[29] !== viewMode || $[30] !== wave) {
    t12 = () => {
      if (!wave || !hasSerialTarget || !showGalleryToggle || viewMode !== "gallery") {
        return;
      }
      setViewMode("chat");
    };
    t13 = [hasSerialTarget, setViewMode, showGalleryToggle, viewMode, wave];
    $[26] = hasSerialTarget;
    $[27] = setViewMode;
    $[28] = showGalleryToggle;
    $[29] = viewMode;
    $[30] = wave;
    $[31] = t12;
    $[32] = t13;
  } else {
    t12 = $[31];
    t13 = $[32];
  }
  useEffect(t12, t13);
  useBreakpoint();
  let t14;
  if ($[33] !== pathname || $[34] !== queryClient || $[35] !== router || $[36] !== searchParams) {
    t14 = drop => {
      queryClient.setQueryData(getDropQueryKey(drop.id), drop as ApiDrop);
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", drop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[33] = pathname;
    $[34] = queryClient;
    $[35] = router;
    $[36] = searchParams;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  const onDropClick = t14;
  let t15;
  if ($[38] !== pathname || $[39] !== router || $[40] !== searchParams) {
    t15 = curationId => {
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      if (curationId) {
        params_1.set("curation", curationId);
      } else {
        params_1.delete("curation");
      }
      const nextQuery = params_1.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      const currentQuery = searchParams.toString();
      const currentUrl = currentQuery ? `${pathname}?${currentQuery}` : pathname;
      if (nextUrl === currentUrl) {
        return;
      }
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[38] = pathname;
    $[39] = router;
    $[40] = searchParams;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  const onSelectCuration = t15;
  if (!wave) {
    return null;
  }
  const t16 = showGalleryToggle ? viewMode : "chat";
  let t17;
  if ($[42] !== enhancedData.firstUnreadSerialNo || $[43] !== onDropClick || $[44] !== t16 || $[45] !== wave) {
    t17 = <MyStreamWaveChat wave={wave} firstUnreadSerialNo={enhancedData.firstUnreadSerialNo} viewMode={t16} onDropClick={onDropClick} />;
    $[42] = enhancedData.firstUnreadSerialNo;
    $[43] = onDropClick;
    $[44] = t16;
    $[45] = wave;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] !== onDropClick || $[48] !== wave) {
    t18 = <MyStreamWaveLeaderboard key={wave.id} wave={wave} onDropClick={onDropClick} />;
    $[47] = onDropClick;
    $[48] = wave;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== onDropClick || $[51] !== wave) {
    t19 = <MyStreamWaveSubmissions wave={wave} onDropClick={onDropClick} />;
    $[50] = onDropClick;
    $[51] = wave;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== wave.id) {
    t20 = <MyStreamWaveSales waveId={wave.id} />;
    $[53] = wave.id;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== onDropClick || $[56] !== wave) {
    t21 = <WaveWinners wave={wave} onDropClick={onDropClick} />;
    $[55] = onDropClick;
    $[56] = wave;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== wave) {
    t22 = <MyStreamWaveOutcome wave={wave} />;
    $[58] = wave;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== onDropClick || $[61] !== wave) {
    t23 = <MyStreamWaveMyVotes wave={wave} onDropClick={onDropClick} />;
    $[60] = onDropClick;
    $[61] = wave;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== wave) {
    t24 = <MyStreamWaveFAQ wave={wave} />;
    $[63] = wave;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== t17 || $[66] !== t18 || $[67] !== t19 || $[68] !== t20 || $[69] !== t21 || $[70] !== t22 || $[71] !== t23 || $[72] !== t24) {
    t25 = {
      [MyStreamWaveTab.CHAT]: t17,
      [MyStreamWaveTab.LEADERBOARD]: t18,
      [MyStreamWaveTab.SUBMISSIONS]: t19,
      [MyStreamWaveTab.SALES]: t20,
      [MyStreamWaveTab.WINNERS]: t21,
      [MyStreamWaveTab.OUTCOME]: t22,
      [MyStreamWaveTab.MY_VOTES]: t23,
      [MyStreamWaveTab.FAQ]: t24
    };
    $[65] = t17;
    $[66] = t18;
    $[67] = t19;
    $[68] = t20;
    $[69] = t21;
    $[70] = t22;
    $[71] = t23;
    $[72] = t24;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  const components = t25;
  let t26;
  if ($[74] !== activeCurationId || $[75] !== onSelectCuration || $[76] !== showGalleryToggle || $[77] !== toggleViewMode || $[78] !== viewMode || $[79] !== wave) {
    t26 = <MyStreamWaveTabs wave={wave} viewMode={viewMode} onToggleViewMode={toggleViewMode} showGalleryToggle={showGalleryToggle} activeCurationId={activeCurationId} onSelectCuration={onSelectCuration} />;
    $[74] = activeCurationId;
    $[75] = onSelectCuration;
    $[76] = showGalleryToggle;
    $[77] = toggleViewMode;
    $[78] = viewMode;
    $[79] = wave;
    $[80] = t26;
  } else {
    t26 = $[80];
  }
  let t27;
  if ($[81] !== activeContentTab || $[82] !== activeCurationId) {
    t27 = activeCurationId ? `my-stream-wave-tabpanel-curation-${activeCurationId}` : getContentTabPanelId(activeContentTab);
    $[81] = activeContentTab;
    $[82] = activeCurationId;
    $[83] = t27;
  } else {
    t27 = $[83];
  }
  let t28;
  if ($[84] !== activeContentTab || $[85] !== activeCurationId || $[86] !== components || $[87] !== onDropClick || $[88] !== wave) {
    t28 = activeCurationId ? <MyStreamWaveCurationContent key={activeCurationId} wave={wave} curationId={activeCurationId} onDropClick={onDropClick} /> : components[activeContentTab];
    $[84] = activeContentTab;
    $[85] = activeCurationId;
    $[86] = components;
    $[87] = onDropClick;
    $[88] = wave;
    $[89] = t28;
  } else {
    t28 = $[89];
  }
  let t29;
  if ($[90] !== t27 || $[91] !== t28) {
    t29 = <div className="tw-relative tw-min-h-0 tw-min-w-0 tw-flex-grow tw-overflow-hidden" role="tabpanel" id={t27}>{t28}</div>;
    $[90] = t27;
    $[91] = t28;
    $[92] = t29;
  } else {
    t29 = $[92];
  }
  let t30;
  if ($[93] !== stableWaveKey || $[94] !== t26 || $[95] !== t29) {
    t30 = <div className="tailwind-scope tw-relative tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col" key={stableWaveKey}>{t26}{t29}</div>;
    $[93] = stableWaveKey;
    $[94] = t26;
    $[95] = t29;
    $[96] = t30;
  } else {
    t30 = $[96];
  }
  return t30;
};
export default MyStreamWaveContent;